import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { companyInfo } from '@/mocks/companyData';

export default function Contact() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    agree: false,
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      setStatus('error');
      setErrorMsg('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }
    if (!formData.agree) {
      setStatus('error');
      setErrorMsg('プライバシーポリシーに同意してください。');
      return;
    }
    setStatus('loading');
    try {
      const body = new URLSearchParams();
      body.append('company', formData.company);
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('phone', formData.phone);
      body.append('subject', formData.subject);
      body.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d8ohf5vij7vns5otivb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus('success');
      setFormData({ company: '', name: '', email: '', phone: '', subject: '', message: '', agree: false });
    } catch {
      setStatus('error');
      setErrorMsg('送信に失敗しました。お手数ですが、admin@3dscarddata.jp まで直接ご連絡ください。');
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-medium tracking-[0.15em] text-primary-600 bg-primary-100/70 px-4 py-1.5 rounded-full mb-6">
              CONTACT
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-6 tracking-wide">
              お問い合わせ
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
              3D's Card Dataのサービスに関するお問い合わせ、資料請求、デモのお申し込みは
              以下のフォームよりお気軽にご連絡ください。
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-10 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Address */}
            <div className="bg-background-50 rounded-2xl p-8 group hover:bg-primary-50 transition-all duration-500 cursor-default">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-200 text-foreground-500 group-hover:bg-primary-100/70 group-hover:text-primary-600 transition-all duration-500 mb-5">
                <i className="ri-map-pin-line text-xl"></i>
              </div>
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 tracking-wide">所在地</h3>
              <p className="text-xs text-foreground-500 leading-relaxed">{companyInfo.address}</p>
              <p className="text-xs text-foreground-400 mt-2">東京メトロ日比谷線 六本木駅 徒歩3分</p>
            </div>

            {/* Phone */}
            <div className="bg-background-50 rounded-2xl p-8 group hover:bg-primary-50 transition-all duration-500 cursor-default">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-200 text-foreground-500 group-hover:bg-primary-100/70 group-hover:text-primary-600 transition-all duration-500 mb-5">
                <i className="ri-phone-line text-xl"></i>
              </div>
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 tracking-wide">お電話</h3>
              <p className="text-lg font-semibold text-foreground-800">{companyInfo.tel}</p>
              <p className="text-xs text-foreground-400 mt-2">受付時間：平日 10:00 - 18:00</p>
            </div>

            {/* Email */}
            <div className="bg-background-50 rounded-2xl p-8 group hover:bg-primary-50 transition-all duration-500">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-200 text-foreground-500 group-hover:bg-primary-100/70 group-hover:text-primary-600 transition-all duration-500 mb-5">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-2 tracking-wide">メール</h3>
              <a href={`mailto:${companyInfo.email}`} className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium">
                {companyInfo.email}
              </a>
              <p className="text-xs text-foreground-400 mt-2">24時間受付（返信は営業時間内）</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 md:gap-16">
            {/* Left: Form */}
            <div className="lg:col-span-3">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-2 tracking-wide">
                お問い合わせフォーム
              </h2>
              <p className="text-xs text-foreground-500 mb-8">
                必須項目は必ずご入力ください。通常2営業日以内にご返信いたします。
              </p>

              {status === 'success' ? (
                <div className="bg-background-100 border border-background-200 rounded-2xl p-10 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 mx-auto mb-6">
                    <i className="ri-check-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">送信完了</h3>
                  <p className="text-sm text-foreground-500 mb-6">
                    お問い合わせいただきありがとうございます。<br />
                    通常2営業日以内にご返信いたします。
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="px-6 py-2.5 text-sm font-medium text-foreground-600 bg-background-200 rounded-full hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    新しいお問い合わせ
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                  {status === 'error' && (
                    <div className="flex items-center gap-3 px-5 py-4 bg-accent-100/70 text-accent-700 rounded-xl text-xs">
                      <i className="ri-error-warning-line text-base"></i>
                      {errorMsg}
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="company" className="block text-xs font-medium text-foreground-600 mb-2">
                        会社名
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 text-sm text-foreground-800 bg-background-50 border border-background-300 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100/50 transition-all duration-300"
                        placeholder="例：株式会社〇〇"
                      />
                    </div>
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-600 mb-2">
                        お名前 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 text-sm text-foreground-800 bg-background-50 border border-background-300 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100/50 transition-all duration-300"
                        placeholder="例：山田 太郎"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-2">
                        メールアドレス <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 text-sm text-foreground-800 bg-background-50 border border-background-300 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100/50 transition-all duration-300"
                        placeholder="例：yamada@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-xs font-medium text-foreground-600 mb-2">
                        電話番号
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 text-sm text-foreground-800 bg-background-50 border border-background-300 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100/50 transition-all duration-300"
                        placeholder="例：03-1234-5678"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-xs font-medium text-foreground-600 mb-2">
                      お問い合わせ種別 <span className="text-accent-500">*</span>
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-sm text-foreground-800 bg-background-50 border border-background-300 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100/50 transition-all duration-300 appearance-none cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="サービスについて">サービスについて</option>
                      <option value="資料請求">資料請求</option>
                      <option value="デモ依頼">デモ依頼</option>
                      <option value="見積依頼">見積依頼</option>
                      <option value="パートナーシップ">パートナーシップ</option>
                      <option value="採用について">採用について</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-600 mb-2">
                      お問い合わせ内容 <span className="text-accent-500">*</span>
                      <span className="text-foreground-400 ml-2">（500文字以内）</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={7}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-sm text-foreground-800 bg-background-50 border border-background-300 rounded-xl focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100/50 transition-all duration-300 resize-none"
                      placeholder="お問い合わせ内容をご記入ください"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-2 text-right">
                      {formData.message.length} / 500
                    </p>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="agree"
                      name="agree"
                      checked={formData.agree}
                      onChange={handleChange}
                      className="mt-0.5 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-300 cursor-pointer"
                    />
                    <label htmlFor="agree" className="text-xs text-foreground-500 leading-relaxed cursor-pointer">
                      <a href="/privacy" className="text-primary-600 hover:text-primary-700 underline">プライバシーポリシー</a>
                      に同意します
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full sm:w-auto px-10 py-3.5 bg-foreground-900 text-background-50 font-medium text-sm tracking-wider rounded-full hover:bg-foreground-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    {status === 'loading' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        送信中...
                      </span>
                    ) : (
                      '送信する'
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Right: Side Info */}
            <div className="lg:col-span-2">
              <div className="sticky top-[100px]">
                <div className="bg-background-100 rounded-2xl p-8 md:p-10 mb-6">
                  <h3 className="font-heading text-base font-bold text-foreground-900 mb-6 tracking-wide">
                    お問い合わせの前に
                  </h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 shrink-0 mt-0.5">
                        <i className="ri-information-line text-sm"></i>
                      </div>
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        ご質問内容によっては、回答にお時間をいただく場合がございます。
                      </p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 shrink-0 mt-0.5">
                        <i className="ri-time-line text-sm"></i>
                      </div>
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        営業時間：平日 10:00〜18:00（土日祝日除く）
                      </p>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 shrink-0 mt-0.5">
                        <i className="ri-phone-line text-sm"></i>
                      </div>
                      <p className="text-xs text-foreground-600 leading-relaxed">
                        お急ぎの場合はお電話にてお問い合わせください。
                      </p>
                    </li>
                  </ul>
                </div>

                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20Japanese%20office%20reception%20area%20with%20warm%20lighting%20minimalist%20design%20wooden%20accents%20green%20plants%20elegant%20corporate%20atmosphere%20professional%20environment%20soft%20gradients%20welcoming%20feel&width=600&height=400&seq=contact-office-side&orientation=landscape"
                    alt="オフィス受付"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}